import React from 'react';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';
import usePage from '../hooks/usePage';
import { useState, useEffect } from 'react';
import API from '../api/axios';

const VALUES = [
  {
    id: 1,
    title: 'Integrity',
    icon: 'fas fa-balance-scale',
    color: 'text-blue-500',
    bg: 'bg-blue-50',
    desc: 'Honesty, fairness and accountability are the foundations of trust. We encourage children to acknowledge mistakes, honour commitments and act with conviction when doing what is right requires courage.'
  },
  {
    id: 2,
    title: 'Respect',
    icon: 'fas fa-hands-helping',
    color: 'text-green-500',
    bg: 'bg-green-50',
    desc: 'Respect begins with recognising the dignity of every individual. Listening attentively, considering different perspectives and caring for shared spaces help students become considerate members of their school community.'
  },
  {
    id: 3,
    title: 'Empathy',
    icon: 'fas fa-heart',
    color: 'text-rose-500',
    bg: 'bg-rose-50',
    desc: 'We encourage children to look beyond their own experiences and consider how others may feel. Recognising someone’s difficulty, offering encouragement and choosing words thoughtfully turn compassion into meaningful action.'
  },
  {
    id: 4,
    title: 'Confidence',
    icon: 'fas fa-star',
    color: 'text-amber-500',
    bg: 'bg-amber-50',
    desc: 'Confidence develops through preparation, participation and constructive encouragement. Students are supported to articulate their ideas, take initiative and approach unfamiliar situations with growing assurance.'
  },
  {
    id: 5,
    title: 'Curiosity',
    icon: 'fas fa-lightbulb',
    color: 'text-purple-500',
    bg: 'bg-purple-50',
    desc: 'An inquisitive mind finds possibilities in everyday questions. We encourage learners to observe closely, investigate thoughtfully and examine evidence, developing intellectual independence.'
  },
  {
    id: 6,
    title: 'Excellence',
    icon: 'fas fa-trophy',
    color: 'text-brand',
    bg: 'bg-brand/10',
    desc: 'Excellence calls for ambition supported by disciplined effort. We inspire students to prepare carefully, pursue challenging goals and refine their work, taking pride in doing things well.'
  },
  {
    id: 7,
    title: 'Resilience',
    icon: 'fas fa-shield-alt',
    color: 'text-teal-500',
    bg: 'bg-teal-50',
    desc: 'Progress requires patience when results fall short of expectations. We help children reflect on difficulties, respond to feedback and adjust their approach, drawing on renewed determination.'
  }
];

const CoreValues = () => {
  const pageData = usePage('core-values');
  const [sectionData, setSectionData] = useState({});

  useEffect(() => {
    API.get('/home-sections').then(res => {
      if (res.data?.data) setSectionData(res.data.data);
    }).catch(console.error);
  }, []);

  const intro = sectionData.core_values_intro || {};
  const heading = intro.heading || 'The 7 Pillars of Character';
  const quote = intro.quote || '“Character is formed in the choices we make, the responsibilities we accept and the respect we extend to others.”';
  const paragraphs = intro.paragraphs || '<p>At <strong>Cambridge International Sr. Sec. School, Bhadana</strong>, our values guide the standards we uphold and the example we set. Through learning, friendships and everyday responsibilities, children discover how these principles shape their conduct, strengthen relationships and give direction to their ambitions.</p>';


  return (
    <>
      <SEO 
        title={pageData.seo?.meta_title || "Our Core Values"} 
        description={pageData.seo?.meta_description || "Discover the core values that guide Cambridge International Sr. Sec. School: Integrity, Respect, Empathy, Confidence, Curiosity, Excellence, and Resilience."} 
      />

      <PageBanner 
        title={pageData.banner_heading || pageData.title || "Our Core Values"} 
        subtitle={pageData.banner_sub_heading || ""} 
        breadcrumbs={[{ label: 'Home', link: '/' }, { label: pageData.title || "Core Values" }]} 
        backgroundImage={pageData.bannerImage} 
        fallback={`${process.env.PUBLIC_URL}/images/page-banners/core-values.jpg`} 
        loading={pageData.loading} 
      />

      {/* Intro & Values Grid Section Combined */}
      <section className="section bg-gray-50/50 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-primary/5 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/5 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl pointer-events-none" />
        
        <div className="container relative z-10">
          <Reveal>
            <div className="text-center mb-16 max-w-4xl mx-auto">
              <div className="kicker mx-auto">Guiding Principles</div>
              <h2 className="h-section text-3xl md:text-4xl font-bold mt-3">{heading}</h2>
              <div className="divider-line mt-5 mb-8 mx-auto" />
              
              <i className="fas fa-quote-left text-4xl text-primary/20 mb-4" />
              <p className="text-xl md:text-2xl font-medium text-gray-800 leading-relaxed italic mb-6">
                {quote}
              </p>
              <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full mb-8" />
              
              <div className="text-body text-[17px] leading-relaxed prose max-w-none mx-auto" dangerouslySetInnerHTML={{ __html: paragraphs }} />
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {VALUES.map((val, index) => (
              <Reveal key={val.id} delayMs={index * 100}>
                <div className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 h-full transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] group relative overflow-hidden">
                  <div className={`absolute top-0 right-0 w-32 h-32 ${val.bg} rounded-bl-full -z-0 opacity-50 transition-transform duration-500 group-hover:scale-110`} />
                  
                  <div className={`w-16 h-16 ${val.bg} ${val.color} rounded-2xl flex items-center justify-center text-2xl mb-6 relative z-10 shadow-sm`}>
                    <i className={val.icon} />
                  </div>
                  
                  <div className="relative z-10">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{val.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-[15px]">
                      {val.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

    </>
  );
};

export default CoreValues;
